import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { scientificPartnerApi } from '@/features/workspace/api';
import {
  CalendarIcon,
  VoteIcon,
  CheckCircle2Icon,
  AwardIcon,
  ClockIcon,
  MapPinIcon,
  UsersIcon,
  ArrowLeftIcon,
  XIcon,
  FileTextIcon,
  AlertCircleIcon
} from './PartnerIcons';

interface EventItem {
  id: string;
  code: string;
  title: string;
  event_type: string;
  start_time: string;
  location?: string;
  is_online: boolean;
  speaker_name?: string;
  speaker_title?: string;
  registered: boolean;
  seats_available: number;
}

interface PollItem {
  id: string;
  code: string;
  title: string;
  category: string;
  deadline: string;
  questions_count: number;
  completed: boolean;
  user_response?: string;
}

export const PartnerEngagementView: React.FC = () => {
  const { notify } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'all' | 'polls' | 'events' | 'registered'>('all');
  const [events, setEvents] = useState<EventItem[]>([]);
  const [polls, setPolls] = useState<PollItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activePoll, setActivePoll] = useState<PollItem | null>(null);
  const [pollAnswer, setPollAnswer] = useState<string>('');
  const [submittingPoll, setSubmittingPoll] = useState<boolean>(false);
  const [calendarNotice, setCalendarNotice] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await scientificPartnerApi.module('engagement', { tab: activeTab });
      if (res && res.items) {
        const evts = (res.items as any[]).filter(i => i.item_type === 'event' || i.event_type);
        const pls = (res.items as any[]).filter(i => i.item_type === 'poll' || i.questions_count !== undefined);
        setEvents(evts);
        setPolls(pls);
      }
    } catch {
      setEvents([]);
      setPolls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const handleToggleEvent = async (event: EventItem) => {
    const nextState = !event.registered;
    try {
      await scientificPartnerApi.registerEvent(event.id, nextState);
      setEvents(prev =>
        prev.map(e => (e.id === event.id ? { ...e, registered: nextState, seats_available: nextState ? e.seats_available - 1 : e.seats_available + 1 } : e))
      );
    } catch {
      notify('Не удалось обновить статус регистрации', 'error');
    }
  };

  const handleSubmitPoll = async () => {
    if (!activePoll || !pollAnswer.trim()) return;
    try {
      setSubmittingPoll(true);
      await scientificPartnerApi.submitPoll(activePoll.id, { answer: pollAnswer.trim() });
      setPolls(prev =>
        prev.map(p => (p.id === activePoll.id ? { ...p, completed: true, user_response: pollAnswer.trim() } : p))
      );
      setActivePoll(null);
      setPollAnswer('');
    } catch {
      notify('Ошибка при сохранении ответа на опрос', 'error');
    } finally {
      setSubmittingPoll(false);
    }
  };

  const handleAddToCalendar = (title: string, date: string) => {
    setCalendarNotice(`Событие "${title}" (${new Date(date).toLocaleDateString('ru-RU')}) добавлено в ваш системный календарь.`);
    setTimeout(() => setCalendarNotice(null), 4000);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border/40 pb-4">
        <div>
          <button
            onClick={() => navigate('/app/partner')}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground mb-2 transition-colors"
          >
            <ArrowLeftIcon size={14} />
            В кабинет научного партнёра
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 text-violet-600 flex items-center justify-center font-bold">
              <VoteIcon size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                Опросы и мероприятия
              </h1>
              <p className="text-xs text-muted-foreground">
                Академические опросы сообщества, экспертные сессии, вебинары и конференции
              </p>
            </div>
          </div>
        </div>
      </div>

      {calendarNotice && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded-xl text-xs flex items-center gap-2">
          <CheckCircle2Icon size={16} className="flex-shrink-0" />
          <span>{calendarNotice}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-xs">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-medium">Активные опросы</span>
            <VoteIcon size={16} className="text-violet-500" />
          </div>
          <div className="text-2xl font-black text-foreground">3</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">2 требуют ответа на этой неделе</div>
        </div>
        <div className="p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-xs">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-medium">Мероприятия в месяце</span>
            <CalendarIcon size={16} className="text-blue-500" />
          </div>
          <div className="text-2xl font-black text-foreground">5</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">3 онлайн-симпозиума, 2 воркшопа</div>
        </div>
        <div className="p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-xs">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-medium">Мои регистрации</span>
            <CheckCircle2Icon size={16} className="text-emerald-500" />
          </div>
          <div className="text-2xl font-black text-foreground">
            {events.filter(e => e.registered).length + 1}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Места подтверждены координатором</div>
        </div>
        <div className="p-4 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm shadow-xs">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-medium">Академический скоринг</span>
            <AwardIcon size={16} className="text-amber-500" />
          </div>
          <div className="text-2xl font-black text-foreground">92/100</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Рейтинг активности в сообществе</div>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-border/40 pb-2">
        {(
          [
            { id: 'all', label: 'Все активности' },
            { id: 'polls', label: 'Опросы' },
            { id: 'events', label: 'Мероприятия' },
            { id: 'registered', label: 'Мои участия' }
          ] as const
        ).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-xs text-muted-foreground">Загрузка данных...</div>
      ) : (
        <div className="space-y-6">
          {(activeTab === 'all' || activeTab === 'events' || activeTab === 'registered') && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <CalendarIcon size={16} className="text-blue-500" />
                Научные мероприятия и сессии
              </h2>
              {events.length === 0 ? (
                <div className="p-8 text-center border border-dashed rounded-xl text-xs text-muted-foreground">
                  Мероприятий в данной категории не найдено
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {events
                    .filter(e => (activeTab === 'registered' ? e.registered : true))
                    .map(evt => (
                      <div
                        key={evt.id}
                        className="p-4 rounded-xl border border-border/60 bg-card hover:border-primary/40 transition-all shadow-xs flex flex-col justify-between"
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="text-[10px] font-mono font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">
                              {evt.code}
                            </span>
                            <span
                              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                evt.is_online
                                  ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                                  : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                              }`}
                            >
                              {evt.is_online ? 'Онлайн (Zoom)' : 'Офлайн'}
                            </span>
                          </div>
                          <h3 className="text-sm font-bold text-foreground leading-snug mb-2">
                            {evt.title}
                          </h3>
                          <div className="space-y-1.5 text-xs text-muted-foreground mb-4">
                            <div className="flex items-center gap-2">
                              <ClockIcon size={14} className="text-muted-foreground/70" />
                              <span>{new Date(evt.start_time).toLocaleString('ru-RU')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPinIcon size={14} className="text-muted-foreground/70" />
                              <span>{evt.location || 'Онлайн-трансляция платформы'}</span>
                            </div>
                            {evt.speaker_name && (
                              <div className="flex items-center gap-2">
                                <UsersIcon size={14} className="text-muted-foreground/70" />
                                <span>
                                  Спикер: <strong className="text-foreground">{evt.speaker_name}</strong>
                                  {evt.speaker_title ? ` (${evt.speaker_title})` : ''}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="cabinet-cta cabinet-cta--violet">
                          <span className="text-[11px] text-muted-foreground">
                            Свободно мест: <strong className="text-foreground">{evt.seats_available}</strong>
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleAddToCalendar(evt.title, evt.start_time)}
                              className="px-2.5 py-1 text-[11px] font-medium border border-border/60 hover:bg-muted/50 rounded-lg text-foreground transition-colors"
                            >
                              В календарь
                            </button>
                            <button
                              onClick={() => handleToggleEvent(evt)}
                              className={`px-3 py-1 text-[11px] font-medium rounded-lg transition-colors ${
                                evt.registered
                                  ? 'bg-rose-500/10 text-rose-600 hover:bg-rose-500/20 border border-rose-500/20'
                                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
                              }`}
                            >
                              {evt.registered ? 'Отменить участие' : 'Участвовать'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {(activeTab === 'all' || activeTab === 'polls') && (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <VoteIcon size={16} className="text-violet-500" />
                Академические опросы и анкетирование
              </h2>
              {polls.length === 0 ? (
                <div className="p-8 text-center border border-dashed rounded-xl text-xs text-muted-foreground">
                  Опросов в данной категории не найдено
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {polls.map(poll => (
                    <div
                      key={poll.id}
                      className="p-4 rounded-xl border border-border/60 bg-card hover:border-violet-500/40 transition-all shadow-xs flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-[10px] font-mono font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">
                            {poll.code}
                          </span>
                          <span
                            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              poll.completed
                                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                            }`}
                          >
                            {poll.completed ? 'Пройден' : 'Требует ответа'}
                          </span>
                        </div>
                        <h3 className="text-sm font-bold text-foreground leading-snug mb-1">
                          {poll.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mb-3">
                          Категория: <span className="text-foreground font-medium">{poll.category}</span>
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                          <div className="flex items-center gap-1.5">
                            <ClockIcon size={14} className="text-muted-foreground/70" />
                            <span>Дедлайн: {new Date(poll.deadline).toLocaleDateString('ru-RU')}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <FileTextIcon size={14} className="text-muted-foreground/70" />
                            <span>Вопросов: {poll.questions_count}</span>
                          </div>
                        </div>

                        {poll.user_response && (
                          <div className="p-2.5 rounded-lg bg-muted/40 border border-border/40 text-xs text-foreground mb-3">
                            <span className="font-semibold text-muted-foreground block text-[10px] uppercase tracking-wider mb-1">
                              Ваш ответ:
                            </span>
                            {poll.user_response}
                          </div>
                        )}
                      </div>

                      <div className="cabinet-cta cabinet-cta--violet">
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <AwardIcon size={14} className="text-amber-500" />
                          +15 баллов скоринга
                        </span>
                        <button
                          onClick={() => {
                            setActivePoll(poll);
                            setPollAnswer(poll.user_response || '');
                          }}
                          className={`px-3 py-1 text-[11px] font-medium rounded-lg transition-colors ${
                            poll.completed
                              ? 'border border-border/60 hover:bg-muted/50 text-foreground'
                              : 'bg-violet-600 hover:bg-violet-700 text-white'
                          }`}
                        >
                          {poll.completed ? 'Изменить ответ' : 'Пройти опрос'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activePoll && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-lg rounded-2xl border border-border shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-border/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <VoteIcon size={20} className="text-violet-500" />
                <h3 className="text-sm font-bold text-foreground">
                  {activePoll.completed ? 'Редактирование ответа' : 'Участие в академическом опросе'}
                </h3>
              </div>
              <button
                onClick={() => setActivePoll(null)}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground"
              >
                <XIcon size={16} />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto">
              <div>
                <div className="text-xs font-mono text-muted-foreground mb-1">{activePoll.code}</div>
                <h4 className="text-base font-bold text-foreground mb-1">{activePoll.title}</h4>
                <p className="text-xs text-muted-foreground">
                  Ваш ответ поможет сформировать аналитический отчёт для совместных публикаций и грантовых заявок.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-foreground">
                  Пожалуйста, изложите ваше экспертное мнение или тезисы:
                </label>
                <textarea
                  rows={5}
                  value={pollAnswer}
                  onChange={e => setPollAnswer(e.target.value)}
                  placeholder="Введите развернутый ответ или предложения по теме..."
                  className="w-full text-xs p-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none text-foreground placeholder:text-muted-foreground"
                />
              </div>

              <div className="flex items-center gap-2 p-3 bg-violet-500/10 border border-violet-500/20 rounded-xl text-xs text-violet-700 dark:text-violet-300">
                <AlertCircleIcon size={16} className="flex-shrink-0" />
                <span>Ответ будет сохранён в едином реестре экспертных мнений Zertteu Academy.</span>
              </div>
            </div>

            <div className="p-4 border-t border-border/50 flex items-center justify-end gap-2 bg-muted/20">
              <button
                onClick={() => setActivePoll(null)}
                className="px-3 py-1.5 text-xs font-medium border border-border rounded-lg hover:bg-muted text-muted-foreground"
              >
                Отмена
              </button>
              <button
                onClick={handleSubmitPoll}
                disabled={submittingPoll || !pollAnswer.trim()}
                className="px-4 py-1.5 text-xs font-semibold bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {submittingPoll ? 'Сохранение...' : 'Отправить ответ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
